import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Room, RoomsResponse, BlockedRoomsResponse, OccupancyResponse, OccupancyGroupBy } from '../models/room.model';
import { WorkloadReportData } from '../models/workload.model';

@Injectable({
    providedIn: 'root'
})
export class RoomsService {
    constructor(private supabase: SupabaseService) { }

    /**
     * Get rooms with pagination
     */
    async getRooms(page: number = 1, roomName: string = '', branchId: number, status: boolean = true, limit: number = 10) {
        const { data, error } = await this.supabase.client
            .rpc('get_offices_paginated', {
                p_branch_id: branchId,
                p_status: status,
                p_office_name: roomName,
                p_page: page,
                p_limit: limit
            });

        return { 
            data: (data && (data as any).data) ? (data as any).data as Room[] : [], 
            total_records: (data && (data as any).total_records) ? (data as any).total_records as number : 0,
            error 
        };
    }

    async getRoomsDirectly() {
        const { data, error } = await this.supabase.client
            .from('offices_tb')
            .select('*');
            
        return { data: data as any[], error };
    }

    /**
     * Get blocked/unavailable rooms
     */
    async getBlockedRooms(
        branchId: number,
        limit: number = 10,
        offset: number = 0,
        startDate: string = '',
        endDate: string = '',
        officeName: string = ''
    ) {
        const { data, error } = await this.supabase.client
            .rpc('get_office_waiting_blocked', {
                p_branch_id: branchId,
                p_limit: limit,
                p_offset: offset,
                p_date_start: startDate,
                p_date_end: endDate,
                p_name: officeName
            });

        return { data: data as BlockedRoomsResponse, error };
    }

    /**
     * Get room occupancy report.
     * groupBy 'room' returns one row per room per day; 'doctor' breaks each of
     * those out per professional working (or booked) in that room that day.
     * includeEmpty keeps days where the room had neither a roster nor a booking
     * (~88% of rows) — off by default so the report shows rooms actually in service.
     */
    async getRoomsOccupancy(
        branchId: number,
        limit: number = 10,
        offset: number = 0,
        startDate: string = '',
        endDate: string = '',
        roomName: string = '',
        groupBy: OccupancyGroupBy = 'room',
        includeEmpty: boolean = false
    ) {
        const { data, error } = await this.supabase.client
            .rpc('get_room_occupancy_with_pagination', {
                p_branch_id: branchId,
                p_limit: limit,
                p_offset: offset,
                p_start_date: startDate,
                p_end_date: endDate,
                p_room_name: roomName,
                p_group_by: groupBy,
                p_include_empty: includeEmpty
            });

        return { data: data as OccupancyResponse, error };
    }

    /**
     * Update room details
     */
    async updateRoom(id: number, roomData: Partial<Room>) {
        const { data, error } = await this.supabase.client
            .from('offices_tb')
            .update(roomData)
            .eq('id', id)
            .select()
            .single();

        return { data, error };
    }

    /**
     * Get single room by ID
     */
    async getRoomById(id: number) {
        const { data, error } = await this.supabase.client
            .from('offices_tb')
            .select('*')
            .eq('id', id)
            .single();

        return { data: data as Room, error };
    }
    /**
     * Doctor workload + room capacity pressure for the Workload tab.
     *
     * Returns the doctor summary, the day-by-day heatmap and the room capacity
     * strip in a single payload: they share a date axis, and splitting them
     * across three calls risks them disagreeing after a filter change.
     *
     * Unlike getRoomsOccupancy this is not paginated — a branch has tens of
     * professionals, not thousands, and the heatmap needs every day in range at
     * once to render a continuous axis.
     *
     * An empty doctorIds means every professional. The payload's
     * `available_doctors` is always the unfiltered roster, so it can populate
     * the filter control without a second call.
     */
    async getWorkloadReport(
        branchId: number,
        startDate: string = '',
        endDate: string = '',
        doctorIds: number[] = []
    ) {
        const { data, error } = await this.supabase.client
            .rpc('get_workload_report_v1', {
                p_branch_id: branchId,
                p_start_date: startDate,
                p_end_date: endDate,
                p_doctor_ids: doctorIds.length ? doctorIds : null
            });

        return { data: data as WorkloadReportData | null, error };
    }

    async createRoom(roomData: Partial<Room>) {
        const { data, error } = await this.supabase.client
            .from('offices_tb')
            .insert(roomData)
            .select()
            .single();

        return { data, error };
    }
}
